import {
  Box,
  Button,
  Header,
  Link,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import PropTypes from 'prop-types';
import React from 'react';

const TX_TYPE_META = {
  produced: { label: 'Produced', type: 'success' },
  shipped: { label: 'Shipped', type: 'stopped' },
  adjust: { label: 'Adjust', type: 'warning' },
};

function DeltaValue({ delta }) {
  if (delta === 0) return <Box color="text-status-inactive">0</Box>;
  const positive = delta > 0;
  return (
    <Box color={positive ? 'text-status-success' : 'text-status-error'}>
      {positive ? `+${delta}` : delta}
    </Box>
  );
}

DeltaValue.propTypes = { delta: PropTypes.number.isRequired };

function TravelerLink({ id }) {
  return (
    <Link href={`/travelers/${id}`} external>
      {id}
    </Link>
  );
}

TravelerLink.propTypes = {
  id: PropTypes.string.isRequired,
};

function TravelersCell({ travelerIds = [], lineItemId = null }) {
  const hasTravelers = travelerIds.length > 0;
  const travelerLabel = `${travelerIds.length} traveler${travelerIds.length === 1 ? '' : 's'}`;
  const orderLink = lineItemId ? (() => {
    const [, orderId, shipmentIdx, lineItemIdx] = lineItemId.split('_');
    return <Link href={`/orders/update/${orderId}#item_${shipmentIdx}_${lineItemIdx}`} external>View order</Link>;
  })() : null;

  if (!hasTravelers && !orderLink) return '—';

  return (
    <SpaceBetween size="xxs">
      {hasTravelers ? (
        <Popover
          position="top"
          size="large"
          triggerType="custom"
          header={travelerLabel}
          content={(
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <SpaceBetween size="xxs">
                {travelerIds.map((id) => (
                  <Box key={id}>
                    <TravelerLink id={id} />
                  </Box>
                ))}
              </SpaceBetween>
            </div>
          )}
        >
          <Button variant="inline-link">
            {travelerLabel}
          </Button>
        </Popover>
      ) : null}

      {orderLink}
    </SpaceBetween>
  );
}

TravelersCell.propTypes = {
  travelerIds: PropTypes.arrayOf(PropTypes.string),
  lineItemId: PropTypes.string,
};

const columnDefinitions = [
  {
    id: 'type',
    header: 'Type',
    cell: (item) => {
      const meta = TX_TYPE_META[item.type] ?? { label: item.type, type: 'info' };
      return <StatusIndicator type={meta.type}>{meta.label}</StatusIndicator>;
    },
  },
  {
    id: 'deltaOnHand',
    header: 'On-hand Δ',
    cell: (item) => <DeltaValue delta={item.deltaOnHand} />,
  },
  {
    id: 'onHandAfter',
    header: 'On-hand after',
    cell: (item) => item.onHandAfter ?? '—',
  },
  {
    id: 'reason',
    header: 'Reason',
    cell: (item) => item.reason || '—',
  },
  {
    id: 'travelerIds',
    header: 'Travelers / Orders',
    cell: (item) => (
      <TravelersCell
        travelerIds={item.travelerIds}
        lineItemId={item.lineItemId}
      />
    ),
  },
  {
    id: 'createdBy',
    header: 'By',
    cell: (item) => item.createdBy?.email || '—',
  },
  {
    id: 'createdAt',
    header: 'Date',
    cell: (item) => item.createdAt?.toDate?.().toLocaleString() || '—',
  },
];

export default function InventoryTxTable({ items, loading = false, pagination = null }) {
  return (
    <Table
      header={<Header variant="h2">Transaction History</Header>}
      items={items}
      loading={loading}
      columnDefinitions={columnDefinitions}
      empty={<Box color="text-status-inactive">No transactions recorded</Box>}
      pagination={pagination}
      wrapLines
    />
  );
}

InventoryTxTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    deltaOnHand: PropTypes.number,
    onHandAfter: PropTypes.number,
    reason: PropTypes.string,
    travelerIds: PropTypes.arrayOf(PropTypes.string),
    lineItemId: PropTypes.string,
    createdBy: PropTypes.shape({ email: PropTypes.string }),
    createdAt: PropTypes.shape({ toDate: PropTypes.func.isRequired }),
  })).isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.node,
};
